import { useState, useEffect, useRef, useCallback } from 'react';
import { formatTime } from './utils';

const useTimer = (expectedScanTime, onTimerUpdate) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerActive, setTimerActive] = useState(true);
  const [timerDisplay, setTimerDisplay] = useState(formatTime(expectedScanTime));
  
  const timerIntervalRef = useRef(null);
  const resetTimerTimeoutId = useRef(null);
  const elapsedTimeRef = useRef(0);

  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);

  const startTimer = useCallback(() => {
    setElapsedTime(0);
    setTimerActive(true);
    setTimerDisplay(formatTime(expectedScanTime));

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      if (timerActive) {
        setElapsedTime(prevTime => {
          const newElapsedTime = prevTime + 0.1;
          elapsedTimeRef.current = newElapsedTime;

          const isOvertime = newElapsedTime >= expectedScanTime;
          const displayTime = isOvertime
            ? -(newElapsedTime - expectedScanTime)
            : expectedScanTime - newElapsedTime;

          setTimerDisplay(formatTime(displayTime));
          
          if (onTimerUpdate) {
            onTimerUpdate(newElapsedTime);
          }

          return newElapsedTime;
        });
      }
    }, 100);
  }, [expectedScanTime, timerActive, onTimerUpdate]);

  const resetTimer = useCallback(() => {
    setTimerActive(false);
    setTimerDisplay(formatTime(expectedScanTime));

    if (resetTimerTimeoutId.current) {
      clearTimeout(resetTimerTimeoutId.current);
    }

    resetTimerTimeoutId.current = setTimeout(() => {
      setElapsedTime(0);
      elapsedTimeRef.current = 0;
      setTimerActive(true);
    }, 50);
  }, [expectedScanTime]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (resetTimerTimeoutId.current) {
        clearTimeout(resetTimerTimeoutId.current);
        resetTimerTimeoutId.current = null;
      }
    };
  }, []);

  return {
    elapsedTime,
    timerActive,
    timerDisplay,
    elapsedTimeRef,
    startTimer,
    resetTimer,
    setElapsedTime
  };
};

export default useTimer;
